import React, { useRef } from 'react';
import { Navigation, Scrollbar } from 'swiper';
import 'swiper/css/navigation';
import 'swiper/css/scrollbar';
import styled, { css } from 'styled-components';
import {
  brandColours,
  breakpointSizes,
  maxBreakpointQuery,
  minBreakpointQuery,
  standardColours,
  visuallyHidden,
  zIndexLayers,
} from '../styles';
import { Container, Heading, Slider, Svg } from './ui';
import arrowIcon from '../images/arrow-icon.inline.svg';

const StyledCardSlider = styled.div`
  overflow: hidden;
`;

const StyledInner = styled.div`
  display: grid;
  grid-template-columns: 100%;

  ${minBreakpointQuery.mlarge`
    grid-template-columns: 1fr 312px;
    column-gap: 20px;
  `}
`;

const StyledHeading = styled(Heading)`
  margin-bottom: 25px;

  ${minBreakpointQuery.small`
    margin-bottom: 30px;
  `}

  ${minBreakpointQuery.large`
    margin-bottom: 35px;
  `}

  ${minBreakpointQuery.xxlarge`
    margin-bottom: 40px;
  `}
`;

const StyledScrollbar = styled.div`
  margin-top: 30px;
  height: 4px;
  max-width: 100%;
  background-color: ${({ alt }) =>
    alt ? brandColours.quaternary : standardColours.lightGrey};
  --swiper-scrollbar-size: 4px;
  --swiper-scrollbar-drag-bg-color: ${({ alt }) =>
    alt ? standardColours.white : brandColours.primary};

  ${maxBreakpointQuery.mlarge`
    order: 3;
  `}

  ${minBreakpointQuery.mlarge`
    grid-column: 2 / 3;
  `}
`;

const StyledSliderWrapper = styled.div`
  ${minBreakpointQuery.mlarge`
    position: relative;
    grid-column: 1 / 3;
  `}
`;

const StyledButton = styled.button`
  ${maxBreakpointQuery.mlarge`
    ${visuallyHidden()};
  `}

  ${minBreakpointQuery.mlarge`
    position: absolute;
    top: 50%;
    z-index: ${zIndexLayers.first};
    display: flex;
    justify-content: center;
    align-items: center;
    height: 75px;
    width: 75px;
    background-color: ${brandColours.primary};
    border: none;
    border-radius: 50%;
  `}

  &:disabled {
    ${minBreakpointQuery.mlarge`
      ${visuallyHidden()};
    `}
  }

  svg {
    ${minBreakpointQuery.mlarge`
      width: 20px;
      height: 20px;
      fill: ${standardColours.white};
    `}
  }

  ${({ next }) => {
    if (next) {
      return css`
        ${minBreakpointQuery.mlarge`
          right: 10px;
          transform: translate(50%, -50%);
        `}

        ${minBreakpointQuery.xxxlarge`
          right: 0;
        `}

        ${minBreakpointQuery.xxxxlarge`
          right: -12.5px;
        `}
      `;
    } else {
      return css`
        ${minBreakpointQuery.mlarge`
          left: 10px;
          transform: translate(-50%, -50%) rotate(180deg);
        `}

        ${minBreakpointQuery.xxxlarge`
          left: 0;
        `}

        ${minBreakpointQuery.xxxxlarge`
          left: -12.5px;
        `}
      `;
    }
  }}
`;

const StyledSlider = styled(Slider)`
  overflow: visible;

  .swiper-slide {
    height: auto;
  }
`;

const CardSlider = ({ heading, alt, isFeaturedProducts, children }) => {
  const prevEl = useRef(null);
  const nextEl = useRef(null);
  const scrollbarEl = useRef(null);

  const sliderOptions = {
    modules: [Navigation, Scrollbar],
    slidesPerView: isFeaturedProducts ? 2 : 1,
    spaceBetween: 10,
    breakpoints: {
      [breakpointSizes.tsmall]: {
        slidesPerView: 2,
        spaceBetween: 10,
      },
      [breakpointSizes.small]: {
        slidesPerView: isFeaturedProducts ? 3 : 2,
        spaceBetween: 15,
      },
      [breakpointSizes.smedium]: {
        slidesPerView: 3,
        spaceBetween: 15,
      },
      [breakpointSizes.medium]: {
        slidesPerView: isFeaturedProducts ? 4 : 3,
        spaceBetween: 20,
      },
      [breakpointSizes.mlarge]: {
        slidesPerView: 4,
        spaceBetween: 20,
      },
      [breakpointSizes.large]: {
        slidesPerView: isFeaturedProducts ? 5 : 4,
        spaceBetween: 25,
      },
    },
    onInit: swiper => {
      swiper.params.navigation.prevEl = prevEl.current;
      swiper.params.navigation.nextEl = nextEl.current;
      swiper.navigation.init();
      swiper.navigation.update();
      swiper.params.scrollbar.el = scrollbarEl.current;
      swiper.params.scrollbar.draggable = true;
      swiper.scrollbar.init();
    },
  };

  return (
    <StyledCardSlider>
      <Container wide={true}>
        <StyledInner>
          <StyledHeading>{heading}</StyledHeading>
          <StyledScrollbar ref={scrollbarEl} alt={alt} />
          <StyledSliderWrapper>
            <StyledButton ref={prevEl} aria-label="Previous">
              <Svg image={arrowIcon} />
            </StyledButton>
            <StyledButton ref={nextEl} next={true} aria-label="Next">
              <Svg image={arrowIcon} />
            </StyledButton>
            <StyledSlider {...sliderOptions}>{children}</StyledSlider>
          </StyledSliderWrapper>
        </StyledInner>
      </Container>
    </StyledCardSlider>
  );
};

export default CardSlider;
